import { useRef, useCallback, useContext, useEffect, useState } from 'react';
import { useFragment, graphql } from 'react-relay';
import { BigNumber } from 'ethers';
import { NftPdpContractContext } from '../../NftPdpContractContext/NftPdpContractContext';
import { useLatestAuctionInfo_nftAuction$key } from './__generated__/useLatestAuctionInfo_nftAuction.graphql';
/**
 * in case dibs-nft goes down, we want to keep a poll asking the auction contract for the latest auction info
 */
export function useLatestAuctionInfo({
    nftAuction: nftAuctionRef,
}: {
    nftAuction: useLatestAuctionInfo_nftAuction$key | null;
}): {
    fetchLatestAuctionInfo: () => void;
    currentBidWei?: string | null;
    auctionStatus?: string | null;
    auctionEndDate?: string | null;
    currentAuctionWinnerAddress?: string | null;
    isAwaitingSettlement: boolean;
} {
    const nftAuction = useFragment(
        graphql`
            fragment useLatestAuctionInfo_nftAuction on NftAuction {
                auctionId
                status
                endDate
                currentBid {
                    wei
                }
                winnerAddress {
                    address
                }
            }
        `,
        nftAuctionRef
    );
    const { auctionId, status, endDate, currentBid, winnerAddress } = nftAuction || {};
    const { wei } = currentBid || {};
    const [currentBidWei, setCurrentBidWei] = useState(wei);
    const [auctionStatus, setAuctionStatus] = useState(status);
    const [auctionEndDate, setAuctionEndDate] = useState(endDate);
    const [currentAuctionWinnerAddress, setCurrentAuctionWinnerAddress] = useState(
        winnerAddress?.address?.toLowerCase()
    );
    const [isAwaitingSettlement, setIsAwaitingSettlement] = useState(false);

    useEffect(() => {
        // keep values updated by polling fallback in sync with the relay subscription
        setCurrentBidWei(wei);
        setAuctionStatus(status);
        setAuctionEndDate(endDate);
        setCurrentAuctionWinnerAddress(winnerAddress?.address?.toLowerCase());
    }, [
        endDate,
        setAuctionEndDate,
        setAuctionStatus,
        setCurrentAuctionWinnerAddress,
        setCurrentBidWei,
        status,
        wei,
        winnerAddress,
    ]);

    useEffect(() => {
        if (
            auctionStatus !== 'ENDED' &&
            auctionEndDate &&
            Date.parse(auctionEndDate) < Date.now()
        ) {
            setIsAwaitingSettlement(true);
        }
    }, [auctionEndDate, auctionStatus]);

    const { auctionContract, setIsAuctionGloballyPaused } = useContext(NftPdpContractContext);
    const dibsNftInterval = useRef<NodeJS.Timeout | null>(null);
    const fetchLatestAuctionInfo = useCallback(async () => {
        try {
            if (auctionContract && auctionId) {
                const isGloballyPaused = await auctionContract.globalPaused();
                setIsAuctionGloballyPaused(isGloballyPaused);
                if (auctionStatus !== 'ENDED') {
                    const auctionStruct = await auctionContract.auctions(auctionId);
                    const hasCurrentBid = !auctionStruct.currentBid.amount.isZero();
                    if (hasCurrentBid) {
                        setCurrentBidWei(auctionStruct.currentBid.amount.toString());
                        setCurrentAuctionWinnerAddress(auctionStruct.currentBidder.toLowerCase());
                    }
                    const hasStartTime = !auctionStruct.startTime.isZero();
                    if (hasStartTime) {
                        const currentEndDate = auctionStruct.startTime
                            .add(auctionStruct.settings.duration)
                            .mul(1000);
                        setAuctionEndDate(new Date(currentEndDate.toNumber()).toString());
                        const currTime = BigNumber.from(Date.now());
                        if (
                            currTime >= auctionStruct.startTime.mul(1000) &&
                            currTime <= currentEndDate
                        ) {
                            setAuctionStatus('LIVE');
                        }
                    }
                    if (auctionStruct.paused) {
                        setAuctionStatus('PAUSED');
                    }
                }
            }
        } catch (e) {
            // silently fail
        }
    }, [auctionContract, auctionId, auctionStatus, setIsAuctionGloballyPaused]);
    useEffect(() => {
        fetchLatestAuctionInfo();
        dibsNftInterval.current = setInterval(() => {
            fetchLatestAuctionInfo();
        }, 30000); // fetch latest auction info from the contract every 30 seconds
        return () => {
            if (dibsNftInterval.current) {
                clearInterval(dibsNftInterval.current);
            }
        };
    }, [fetchLatestAuctionInfo]);

    return {
        fetchLatestAuctionInfo,
        currentBidWei,
        auctionStatus,
        auctionEndDate,
        currentAuctionWinnerAddress,
        isAwaitingSettlement,
    };
}
